import './assets/default.css';
import 'react-resizable/css/styles.css';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import 'react-quill/dist/quill.snow.css';
import "./app.css";
import "./styles/all-styles.css"

import {BrowserRouter, Route, Routes, Navigate, useLocation, useNavigate} from 'react-router-dom';
import React, { Suspense, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { notification } from 'antd';
import { useDispatch } from 'react-redux';
import { setNotification } from './middleware/redux/reducers/notification.reducer';
import { ApolloProvider, useLazyQuery, useQuery } from '@apollo/client';
import { privateClient, publicClient } from './config/apollo';
import { useErrorBoundary } from './util/errorBoundary/errorboundary';
import { ErrorFallback } from './util/errorFallback/errorFallback';
import { isLoginCheckQuery } from './util/query/user.query';
import { PrivateRoutes } from './util/routes/private.routes';
import Spinner from './components/spinner';

// Lazy load all components
const Login = React.lazy(() => import("./pages/login").then(module => ({ default: module.Login })));
const DefaultLayout = React.lazy(() => import("./layout/defaultLayout").then(module => ({ default: module.DefaultLayout })));
const Employee = React.lazy(() => import("./pages/employee").then(module => ({ default: module.Employee })));
const Branch = React.lazy(() => import("./pages/branch").then(module => ({ default: module.Branch })));
const EditForm = React.lazy(() => import("./pages/editBranchForm/editForm.page").then(module => ({ default: module.EditForm })));
const FormView = React.lazy(() => import('./pages/formView/formView').then(module => ({ default: module.FormView })));
const BranchDetailPage = React.lazy(() => import('./pages/branchDetailPage/branchDetailPage').then(module => ({ default: module.BranchDetailPage })));
const AllProperties = React.lazy(() => import('./pages/allProperties/allProperties.page').then(module => ({ default: module.AllProperties })));
const BranchAllPropHistory = React.lazy(() => import('./pages/branchAllPropHistory/branchAllPropHistory.page').then(module => ({ default: module.BranchAllPropHistory })));
const SiteGroup = React.lazy(() => import('./pages/sitegroup/sitegroup.page').then(module => ({ default: module.SiteGroup })));
const EditEmployeeForm = React.lazy(() => import('./pages/employee/editEmployeeForm/editEmployeeForm').then(module => ({ default: module.EditEmployeeForm })));
const EmployeeDetailPage = React.lazy(() => import('./pages/employeeDetailPage/employeeDetail.page').then(module => ({ default: module.EmployeeDetailPage })));
const EmployeeAllPropHistory = React.lazy(() => import('./pages/employeeAllPropHistory/employeeAllPropHistory').then(module => ({ default: module.EmployeeAllPropHistory })));
const Password = React.lazy(() => import('./pages/login/password').then(module => ({ default: module.Password })));
const UnAuthroizedAccess = React.lazy(() => import('./pages/unAuthroizedAccess/unAuthroizedAccess.page').then(module => ({ default: module.UnAuthroizedAccess })));
const Join = React.lazy(() => import('./pages/join/join').then(module => ({ default: module.Join })));
const UserPerference = React.lazy(() => import('./pages/setting/userPerference/userPerference').then(module => ({ default: module.UserPerference })));
const SiteGroupDetailPage = React.lazy(() => import('./pages/sitegroup/sitegroupDetailPage/sitegroupDetail.page').then(module => ({ default: module.SiteGroupDetailPage })));
const CustomerPage = React.lazy(() => import('./pages/customer/customer.page').then(module => ({ default: module.CustomerPage })));
const CustomerDetailPage = React.lazy(() => import('./pages/customer/customerDetailPage/customerDetail.page').then(module => ({ default: module.CustomerDetailPage })));
const AgencyPage = React.lazy(() => import('./pages/agency/agency.page').then(module => ({ default: module.AgencyPage })));
const AgencyDetailPage = React.lazy(() => import('./pages/agency/agencyDetailPage/agencyDetail.page').then(module => ({ default: module.AgencyDetailPage })));
const SitePage = React.lazy(() => import('./pages/site/site.page').then(module => ({ default: module.SitePage })));
const SiteDetailPage = React.lazy(() => import('./pages/site/siteDetailPage/siteDetail.page').then(module => ({ default: module.SiteDetailPage })));
const SchedulePage = React.lazy(() => import('./pages/schedule/schedule.page').then(module => ({ default: module.SchedulePage })));
const TaskPage = React.lazy(() => import('./pages/task/task.page').then(module => ({ default: module.TaskPage })));
const ExternalSiteLog = React.lazy(() => import('./pages/externalSiteLogPage/externalSiteLog.page').then(module => ({ default: module.ExternalSiteLog })));
const FilePreview = React.lazy(() => import('./pages/filePreview/filePreview').then(module => ({ default: module.FilePreview })));
const ShareDocument = React.lazy(() => import('./pages/shareDocument/shareDocument').then(module => ({ default: module.ShareDocument })));
const ExternalFilePreview = React.lazy(() => import('./pages/externalFilePreview/externalFilePreview').then(module => ({ default: module.ExternalFilePreview })));
const TimelinePage = React.lazy(() => import('./pages/timeline/timeline.page').then(module => ({ default: module.TimelinePage })));
const PublicAccessDutyReport = React.lazy(() => import('./pages/schedule/publicAccessDutyReport/publicAccessDutyReport').then(module => ({ default: module.PublicAccessDutyReport })));
const PublicAccessDutyReportLanding = React.lazy(() => import('./pages/schedule/publicAccessDutyReport/PublicAccessDutyReportLanding').then(module => ({ default: module.PublicAccessDutyReportLanding })));
const WardSecurityMobileReport = React.lazy(() => import('./reports/mobileReports/wardSecurity/wardSecurityMobileReport').then(module => ({ default: module.WardSecurityMobileReport })));
const SystemReport = React.lazy(() => import('./pages/systemReports/systemReports.page'));
const SystemReportPayBillView = React.lazy(() => import('./pages/systemReports/reportViews/systemReportPayBillView').then(module => ({ default: module.SystemReportPayBillView })));
const SystemReportEmployeePayDetailView = React.lazy(() => import('./pages/systemReports/reportViews/systemReportEmployeePayDetail').then(module => ({ default: module.SystemReportEmployeePayDetailView })));
const SystemReportEmployeePaySummaryView = React.lazy(() => import('./pages/systemReports/reportViews/systemReportEmployeePaySummary').then(module => ({ default: module.SystemReportEmployeePaySummaryView })));
const SystemReportBillDetailedView = React.lazy(() => import('./pages/systemReports/reportViews/billDetailReport').then(module => ({ default: module.SystemReportBillDetailedView })));
const SystemReportSiteScheduleView = React.lazy(() => import('./pages/systemReports/reportViews/siteScheduleReport').then(module => ({ default: module.SystemReportSiteScheduleView })));
const SystemReportSiteScheduleWeeklyView = React.lazy(() => import('./pages/systemReports/reportViews/siteScheduleWeeklyReport').then(module => ({ default: module.SystemReportSiteScheduleWeeklyView })));
const AttandanceGapPage = React.lazy(() => import('./pages/attandancegapPage/attandanceGap.page').then(module => ({ default: module.AttandanceGapPage })));

// Loading component for Suspense
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spinner />
  </div>
);

const useNetworkStatus = () => {
  const [effectiveType, setEffectiveType] = useState(navigator.connection?.effectiveType || "unknown");

  useEffect(() => {
    const updateNetworkInfo = () => {
      setEffectiveType(navigator.connection.effectiveType);
    };

    navigator.connection?.addEventListener("change", updateNetworkInfo);

    return () => {
      navigator.connection?.removeEventListener("change", updateNetworkInfo);
    };
  }, []);

  return effectiveType; // e.g., '4g', '3g', '2g', 'slow-2g'
};

function App() {
  const dispatch = useDispatch();
  const [isOffline, setIsOffline] = useState(true);

  useEffect(() => {
    dispatch(setNotification({}));
    setInterval(() => {  
      if (navigator.onLine) {
        setIsOffline(true)
      } else {
        setIsOffline(false);
      }
    }, 100);
  }, [dispatch]);

  useEffect(() => {
    const checkScreen = () => {
      // const isSmallScreen = window.innerWidth <= 1550;
      const isSmallScreen = window.innerWidth <= 1690;
  
      if (isSmallScreen) {
        document.body.classList.add("mac-small");
      } else {
        document.body.classList.remove("mac-small");
      }
    };
  
    checkScreen(); // Check initially
    window.addEventListener("resize", checkScreen);
  
    return () => window.removeEventListener("resize", checkScreen); // Cleanup
  }, []);
  
  const {notificationToast} = useSelector(state => state.notificationReducer);
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    if(Object.keys(notificationToast).length > 0){
      if(notificationToast?.error){
        api.error({
          message: notificationToast.message,
          placement: notificationToast?.placement || "top",
          className: 'notification-without-close',
        });
      } else {
        api.success({
          message: notificationToast.message,
          placement: notificationToast?.placement || "top",
          className: 'notification-without-close',
        });
      }
      dispatch(setNotification({}));
    }
  }, [notificationToast, api, dispatch]);

  const hasError = useErrorBoundary();

  useEffect(() => {
    // Check if the browser supports the Notification API
    if (!('Notification' in window)) {
      console.error('This browser does not support notifications.');
      return;
    }

    // Request permission for notifications
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        console.log('Notification permission granted.');
      } else {
        console.error('Notification permission denied.');
      }
    });
  }, []);

  const networkStatus = useNetworkStatus();

  return (
    hasError ? 
    <ErrorFallback /> :
    <>
      {contextHolder}

      {(!isOffline || ["2g", "slow-2g", "3g"].includes(networkStatus)) &&
        <div className='offline'>
          {
            !isOffline ? "Your are currently offline. reconnecting ..." : 
            ["2g", "slow-2g", "3g"].includes(networkStatus) ? "⚠️ Your internet connection is unstable! You may experience interruptions in real-time updates." : ""
          }
        </div>
      }

      <BrowserRouter>
        {/* public routes */}
        <ApolloProvider client={publicClient}>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {localStorage.getItem('authToken') ? 
                <Route path='/' element={<Navigate to={"/user/employee"} />} /> :
                <Route path='/' element={<Login key="login"/>} />
              }
              <Route path="/pwd" element={<Password key="pwd"/>} />
              <Route path="/site/:siteId/:siteName/log" element={<ExternalSiteLog />} />

              {/* schedule report */}
              <Route path='/schedulereporting/:templateId/:scheduleId/:siteId' element={<PublicAccessDutyReportLanding />} />
              <Route path='/schedulereport' element={<PublicAccessDutyReport />} />
              <Route path="/join/:employeeId" element={<Join />} />
              <Route path="/preview" element={<ExternalFilePreview />} />

              {/* specific customer report design */}
              <Route path='wardreport/:jobId' exact element={<WardSecurityMobileReport />} />
            </Routes>
          </Suspense>
        </ApolloProvider>

        {/* private Routes */}
        <ApolloProvider client={privateClient}>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Error Fall back route */}
              <Route path="/error" exact element={<UnAuthroizedAccess />} />
              
              <Route path='/formview' element={<FormView />} />
              <Route path='/branch/editform' exact element={<EditForm />} />
              <Route path='/employee/editform' exact element={<EditEmployeeForm />} />

              {/* private routes */}
              <Route path='/user/' element={
                <PrivateRoutes>
                  <DefaultLayout />
                </PrivateRoutes>
              }>
                {/* System reports */}
                <Route path='sysreport/:category?' exact element={<SystemReport />} />
                <Route path='sysreport/paybill' exact element={<SystemReportPayBillView />} />
                <Route path='sysreport/employeePayDetail' exact element={<SystemReportEmployeePayDetailView />} />
                <Route path='sysreport/employeePaySummary' exact element={<SystemReportEmployeePaySummaryView />} />
                <Route path='sysreport/billDetailedReport' exact element={<SystemReportBillDetailedView />} />
                <Route path='sysreport/siteSchedule' exact element={<SystemReportSiteScheduleView />} />
                <Route path='sysreport/siteScheduleWeekly' exact element={<SystemReportSiteScheduleWeeklyView />} />
                
                <Route path='perference' exact element={<UserPerference />} />
                <Route path='schedule' exact element={<SchedulePage />} />
                <Route path='preview' exact element={<FilePreview />} />
                <Route path='share/:fileId' exact element={<ShareDocument />} />

                <Route path='branch' exact element={<Branch />} />
                <Route path='branch-detail/:id' element={<BranchDetailPage />} />
                <Route path='branch-detail/:id/:edit?' element={<BranchDetailPage />} />
                <Route path='branch-detail/:id/tab/:tab?' element={<BranchDetailPage />} />
                <Route path='branch-detail/:id/tab/:tab?/:folderId?' element={<BranchDetailPage />} />

                <Route path='prophistory' element={<BranchAllPropHistory />} />

                <Route path='employee' element={<Employee />} />
                <Route path='employee-detail/:id/:edit?' element={<EmployeeDetailPage />} />
                <Route path='employee-detail/:id/tab/:tab?/:documentId?' element={<EmployeeDetailPage />} />
                <Route path='employee-detail/:id/tab/:tab?/folder/:documentId?' element={<EmployeeDetailPage />} />

                <Route path='employee-prop-history/:id' element={<EmployeeAllPropHistory />} />
                <Route path='allproperties' element={<AllProperties />} />
                
                {/* site group */}
                <Route path='sitegroup' element={<SiteGroup />} />
                <Route path='sitegroup-detail/:id/:edit?' element={<SiteGroupDetailPage />} />
                <Route path='sitegroup-detail/:id/tab/:tab' element={<SiteGroupDetailPage />} />

                {/* site */}
                <Route path='site' element={<SitePage />} />
                <Route path='site-detail/:id/:edit?' element={<SiteDetailPage />} />
                <Route path='site-detail/:id/tab/:tab/:operationTab?' element={<SiteDetailPage />} />
                <Route path='site-detail/:id/tab/:tab?/folder/:documentId?' element={<SiteDetailPage />} />

                {/* Customer */}
                <Route path='customer' element={<CustomerPage />} />
                <Route path='customer-detail/:id' element={<CustomerDetailPage />} />
                <Route path='customer-detail/:id/:edit?' element={<CustomerDetailPage />} />
                <Route path='customer-detail/:id/tab/:tab' element={<CustomerDetailPage />} />
                <Route path='customer-detail/:id/tab/:tab?/folder/:documentId?' element={<CustomerDetailPage />} />

                {/* agency */}
                <Route path='agency' element={<AgencyPage />} />
                <Route path='agency-detail/:id/:edit?' element={<AgencyDetailPage />} />
                <Route path='agency-detail/:id/tab/:tab' element={<AgencyDetailPage />} />
                <Route path='agency-detail/:id/tab/:tab?/folder/:documentId?' element={<AgencyDetailPage />} />

                {/* Task */}
                <Route path='task' element={<TaskPage />} />
                <Route path='timeline' element={<TimelinePage />} />
                <Route path='attandancegap' element={<AttandanceGapPage />} />
              </Route>
            </Routes>
          </Suspense>
        </ApolloProvider>
      </BrowserRouter>
    </>
  );
}

export default App;